import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Modal, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import userClientAssignmentService from '../../services/userClientAssignmentService';
import Avatar from '../common/Avatar';

const UserClientAssignments = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matrix, setMatrix] = useState({ users: [], clients: [] });
  const [summary, setSummary] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [searchClient, setSearchClient] = useState('');
  const [processingCell, setProcessingCell] = useState(null); // Para mostrar spinner en celda específica

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [matrixData, summaryData] = await Promise.all([
        userClientAssignmentService.getMatrix(),
        userClientAssignmentService.getSummary(),
      ]);
      setMatrix(matrixData);
      setSummary(summaryData);
    } catch (error) {
      toast.error('Error al cargar las asignaciones');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setSelectedClients(user.assignedClients || []);
    setShowModal(true);
  };

  const handleToggleClient = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    setSelectedClients(matrix.clients.map(c => c.id));
  };

  const handleDeselectAll = () => {
    setSelectedClients([]);
  };

  const handleSaveAssignments = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      await userClientAssignmentService.syncAssignments(selectedUser.id, selectedClients);
      toast.success(`Asignaciones actualizadas para ${selectedUser.fullName}`);
      setShowModal(false);
      await loadData();
    } catch (error) {
      toast.error('Error al guardar las asignaciones');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleQuickToggle = async (userId, clientId, currentlyAssigned) => {
    const cellKey = `${userId}-${clientId}`;
    setProcessingCell(cellKey);
    
    try {
      if (currentlyAssigned) {
        await userClientAssignmentService.deleteByUserAndClient(userId, clientId);
      } else {
        await userClientAssignmentService.create(userId, clientId);
      }
      
      // Actualizar el estado local sin recargar toda la tabla
      setMatrix(prevMatrix => ({
        ...prevMatrix,
        users: prevMatrix.users.map(user => {
          if (user.id === userId) {
            const newAssignedClients = currentlyAssigned
              ? user.assignedClients.filter(id => id !== clientId)
              : [...(user.assignedClients || []), clientId];
            return { ...user, assignedClients: newAssignedClients };
          }
          return user;
        })
      }));

      // Actualizar el resumen de estadísticas
      setSummary(prev => {
        if (!prev) return prev;
        const newTotalAssignments = currentlyAssigned 
          ? prev.totalAssignments - 1 
          : prev.totalAssignments + 1;
        return { ...prev, totalAssignments: newTotalAssignments };
      });

      toast.success(currentlyAssigned ? 'Asignación removida' : 'Cliente asignado');
    } catch (error) {
      toast.error('Error al actualizar la asignación');
      console.error(error);
    } finally {
      setProcessingCell(null);
    }
  };

  const filteredUsers = matrix.users.filter(u => 
    u.fullName.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.username.toLowerCase().includes(searchUser.toLowerCase())
  );

  const filteredClients = matrix.clients.filter(c =>
    c.name.toLowerCase().includes(searchClient.toLowerCase())
  );

  if (loading) {
    return (
      <Container fluid className="px-3">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Cargando asignaciones...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        {/* Header */}
        <div className="mb-4 card card-body">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-2">
                <FontAwesomeIcon icon="user-tag" className="me-2 text-primary" />
                Asignación de Clientes a Usuarios
              </h2>
              <h5 className="text-700 fw-semi-bold">
                Gestiona qué clientes puede ver cada usuario del sistema
              </h5>
            </Col>
            <Col xs="auto">
              <Button variant="outline-primary" onClick={loadData}>
                <FontAwesomeIcon icon="sync" className="me-1" />
                Actualizar
              </Button>
            </Col>
          </Row>
        </div>

        {/* Estadísticas */}
        {summary && (
          <Row className="g-3 mb-4">
            <Col lg={3} md={6}>
              <Card className="bg-primary text-white">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 className="text-white mb-0">{summary.totalAssignments}</h4>
                      <small>Asignaciones Totales</small>
                    </div>
                    <FontAwesomeIcon icon="link" size="2x" className="opacity-50" />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card className="bg-success text-white">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 className="text-white mb-0">{summary.usersWithAssignments}</h4>
                      <small>Usuarios con Clientes</small>
                    </div>
                    <FontAwesomeIcon icon="users" size="2x" className="opacity-50" />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card className="bg-info text-white">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 className="text-white mb-0">{summary.clientsWithUsers}</h4>
                      <small>Clientes Asignados</small>
                    </div>
                    <FontAwesomeIcon icon="building" size="2x" className="opacity-50" />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card className="bg-warning text-white">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 className="text-white mb-0">{matrix.clients.length}</h4>
                      <small>Total Clientes</small>
                    </div>
                    <FontAwesomeIcon icon="briefcase" size="2x" className="opacity-50" />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Filtros */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    <FontAwesomeIcon icon="search" className="me-1 text-primary" />
                    Buscar Usuario
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nombre o usuario..."
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    <FontAwesomeIcon icon="search" className="me-1 text-info" />
                    Buscar Cliente
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nombre del cliente..."
                    value={searchClient}
                    onChange={(e) => setSearchClient(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Matriz de Asignaciones */}
        <Card className="shadow-sm">
          <Card.Header className="bg-light">
            <h6 className="mb-0">
              <FontAwesomeIcon icon="table" className="me-2 text-primary" />
              Matriz de Asignaciones
            </h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <table className="table table-sm table-bordered mb-0" style={{ fontSize: '12px' }}>
                <thead className="bg-light sticky-top">
                  <tr>
                    <th style={{ minWidth: '200px', position: 'sticky', left: 0, background: '#f8f9fa', zIndex: 2 }}>
                      Usuario
                    </th>
                    {filteredClients.map(client => (
                      <th key={client.id} className="text-center" style={{ minWidth: '80px', writingMode: 'vertical-rl', textOrientation: 'mixed', height: '120px' }}>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{client.name}</Tooltip>}
                        >
                          <span className="d-inline-block" style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {client.name}
                          </span>
                        </OverlayTrigger>
                      </th>
                    ))}
                    <th className="text-center" style={{ minWidth: '100px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id}>
                      <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                        <div className="d-flex align-items-center">
                          <Avatar size="s" name={user.fullName} className="me-2" />
                          <div>
                            <div className="fw-semibold">{user.fullName}</div>
                            <small className="text-muted">@{user.username}</small>
                            {user.isSuperAdmin && (
                              <Badge bg="danger" className="ms-1" style={{ fontSize: '9px' }}>
                                Super Admin
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      {filteredClients.map(client => {
                        const isAssigned = user.assignedClients?.includes(client.id);
                        const cellKey = `${user.id}-${client.id}`;
                        const isProcessing = processingCell === cellKey;
                        return (
                          <td 
                            key={client.id} 
                            className="text-center align-middle"
                            style={{ 
                              cursor: user.isSuperAdmin || isProcessing ? 'not-allowed' : 'pointer',
                              backgroundColor: isProcessing ? '#f0f0f0' : undefined
                            }}
                            onClick={() => !user.isSuperAdmin && !isProcessing && handleQuickToggle(user.id, client.id, isAssigned)}
                          >
                            {isProcessing ? (
                              <Spinner animation="border" size="sm" variant="primary" />
                            ) : user.isSuperAdmin ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Super Admin tiene acceso a todos los clientes</Tooltip>}
                              >
                                <FontAwesomeIcon icon="star" className="text-warning" />
                              </OverlayTrigger>
                            ) : isAssigned ? (
                              <FontAwesomeIcon icon="check-circle" className="text-success" />
                            ) : (
                              <FontAwesomeIcon icon="circle" className="text-light" />
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center">
                        {!user.isSuperAdmin && (
                          <Button
                            variant="falcon-default"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                            title="Editar asignaciones"
                          >
                            <FontAwesomeIcon icon="edit" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>

        {/* Leyenda */}
        <Card className="mt-3">
          <Card.Body>
            <div className="d-flex gap-4 align-items-center flex-wrap">
              <span className="text-muted">Leyenda:</span>
              <span>
                <FontAwesomeIcon icon="check-circle" className="text-success me-1" />
                Asignado
              </span>
              <span>
                <FontAwesomeIcon icon="circle" className="text-light me-1" />
                No asignado
              </span>
              <span>
                <FontAwesomeIcon icon="star" className="text-warning me-1" />
                Super Admin (todos los clientes)
              </span>
              <small className="text-muted ms-auto">
                Haz clic en una celda para asignar/desasignar rápidamente
              </small>
            </div>
          </Card.Body>
        </Card>

        {/* Modal de Edición */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon="user-tag" className="me-2 text-primary" />
              Asignar Clientes a {selectedUser?.fullName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <Badge bg="primary" className="me-2">
                  {selectedClients.length} seleccionados
                </Badge>
                <Badge bg="secondary">
                  {matrix.clients.length} total
                </Badge>
              </div>
              <div>
                <Button variant="outline-success" size="sm" className="me-2" onClick={handleSelectAll}>
                  <FontAwesomeIcon icon="check-double" className="me-1" />
                  Seleccionar Todos
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleDeselectAll}>
                  <FontAwesomeIcon icon="times" className="me-1" />
                  Limpiar
                </Button>
              </div>
            </div>
            <div className="row g-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {matrix.clients.map(client => (
                <div key={client.id} className="col-md-6 col-lg-4">
                  <div 
                    className={`p-2 border rounded cursor-pointer ${selectedClients.includes(client.id) ? 'bg-primary text-white border-primary' : 'bg-light'}`}
                    onClick={() => handleToggleClient(client.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon 
                        icon={selectedClients.includes(client.id) ? 'check-square' : 'square'} 
                        className="me-2"
                      />
                      <div>
                        <div className="fw-semibold">{client.name}</div>
                        {client.subdomain && (
                          <small className={selectedClients.includes(client.id) ? 'text-white-50' : 'text-muted'}>
                            {client.subdomain}.galaxymetrics.com
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} disabled={saving}>
              <FontAwesomeIcon icon="times" className="me-1" />
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSaveAssignments} disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="save" className="me-1" />
                  Guardar Asignaciones
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
};

export default UserClientAssignments;
